<template>
    <TabNavigation :is-active="taskStatus.relist.status" />
    <div class="p-0 h-full w-full z-[9999999] overflow-y-auto overflow-x-hidden" v-show="!hideUI">
        <Card class="p-0 bg-gray-100">
            <template #content>


                <InlineMessage severity="warn" icon=" " :closable="false">
                    <strong>CAUTION:</strong> If you use a third-party crosslisting or inventory tool, PoshPop's Relist
                    creates a
                    <strong>NEW</strong> Poshmark listing. This breaks the connection to the original listing, so when
                    you mark the item as sold in that tool, it will <strong>NOT</strong> be able to delist the correct
                    Poshmark listing.
                </InlineMessage>


                <Card class="mt-4 p-0">
                    <template #title>
                        <div class="flex items-center">
                            <h6 class="text-base">
                                Relist
                            </h6>
                            <InfoTip class="ml-2" tip="Relist uses Poshmark’s Copy Listing feature to replace the Original Listing.
                                Under Poshmark’s Excessive Listing Policy, only items listed for at least 61 days can be relisted while keeping all likers, and the new listing will appear under “Just In” for increased exposure.
                                Poshmark’s relist limits are approximately 150 listings per day and 40 per hour.">
                            </InfoTip>
                        </div>
                    </template>
                    <template #content>
                        <div v-if="validations.has('relist')">
                            <Message severity="error" v-for="(errorMessage, index) of validations.get('relist')"
                                :keys="errorMessage" icon="null" :closable="false">
                                {{ errorMessage }}
                            </Message>
                        </div>
                        <div class="py-3">
                            <h6 class="text-base">Time Delay</h6>
                            <div class="flex justify-between w-full">
                                <div class="flex items-center mb-2">
                                    <InputNumber v-model="delayRange[0]" :min="0" :max="delayRange[1] - 1"
                                        inputClass="!w-[30px] text-center" :disabled="taskStatus.relist.status" />
                                    <span>Sec</span>
                                </div>
                                <div class="flex items-center mb-2">
                                    <InputNumber v-model="delayRange[1]" :min="delayRange[0] + 1" :max="60"
                                        inputClass="!w-[30px] text-center" :disabled="taskStatus.relist.status" />
                                    <span>Sec</span>
                                </div>
                            </div>
                            <Slider v-on:update:model-value="updateSliderValue" :pt="sliderStyles" v-model="delayRange"
                                range :step="1" ariaLabel="sec" :min="1" :max="50" class="mx-2"
                                :disabled="taskStatus.relist.status" />
                        </div>
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center gap-x-3">
                                <span class="ml-2">Relist items older than</span>
                                <InputNumber :min="1" placeholder="61"
                                    :input-class="'w-[80px] p-3 border border-[#0070b7] rounded-lg '"
                                    v-model="listingsOlderThan" inputId="listingsOlderThan" :useGrouping="true"
                                    :disabled="taskStatus.relist.status" />
                                <span>days</span>
                            </div>
                        </div>

                    </template>
                </Card>
                <Card class="mt-4 p-0">
                    <template #title>
                        <h6 class="text-base">Number of Items to Relist</h6>
                    </template>
                    <template #content>
                        <div v-if="validations.has('itemsToRelist')">
                            <Message severity="error" v-for="(errorMessage, index) of validations.get('itemsToRelist')"
                                :keys="errorMessage" icon="null" :closable="false">
                                {{ errorMessage }}
                            </Message>
                        </div>
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center">
                                <div>
                                    <label for="someProducts" class="ml-2 hover:cursor-pointer">
                                        Max number of daily relists
                                    </label>
                                    <InputNumber :input-class="'w-[80px] p-3 border border-[#0070b7] rounded-lg '"
                                        v-model="maxRelistsPerDay" inputId="maxRelistsPerDay" :min="1"
                                        :useGrouping="true" :disabled="taskStatus.relist.status" />
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col gap-3 mt-4">
                            <div class="flex items-center" @click="limit = 0">
                                <RadioButton v-model="allProducts" inputId="allProducts" name="limitCheck" :value="true"
                                    :disabled="taskStatus.relist.status" />
                                <label for="allProducts" class="ml-2 hover:cursor-pointer">Relist all matching
                                    items</label>
                            </div>
                            <div class="flex items-center">
                                <RadioButton v-model="allProducts" inputId="someProducts" name="limitCheck"
                                    :value="false" :disabled="taskStatus.relist.status" />
                                <div>
                                    <label for="someProducts" class="ml-2 hover:cursor-pointer">
                                        Relist set number of items
                                    </label>
                                    <InputNumber placeholder="0"
                                        :input-class="'w-[80px] p-3 border border-[#0070b7] rounded-lg '"
                                        v-model="limit" inputId="limit" :useGrouping="true" @click="allProducts = false"
                                        :disabled="taskStatus.relist.status" />
                                </div>
                            </div>
                        </div>
                    </template>
                </Card>

                <div class="flex justify-end">
                    <div class="flex justify-end mt-5 gap-x-4">
                        <Button v-if="taskStatus.relist.status" @click="requireConfirmation('top')"
                            class="border border-red-500 font-bold px-8 py-1 bg-red-500 text-white rounded hover:cursor-pointer hover:bg-white hover:text-red-500">
                            Stop Current Task
                        </Button>
                        <Button v-else
                            class="border border-[#0070b7] font-bold bg-[#0070b7] text-white p-4 rounded hover:cursor-pointer hover:bg-white hover:text-[#0070b7]"
                            @click="validateRelistRequest()">
                            Start
                            <div v-if="!authStore?.loggedIn"
                                class="bg-white rounded-full w-5 h-5 flex items-center justify-center ml-2">
                                <i class="pi pi-user text-[#0070b7] font-normal text-sm"></i>
                            </div>
                            <div v-else-if="!authStore?.hasActivePlan()"
                                class="bg-white rounded-full w-5 h-5 flex items-center justify-center ml-2">
                                <i class="pi pi-dollar text-[#0070b7] font-normal text-sm"></i>
                            </div>
                        </Button>
                    </div>
                </div>
            </template>
        </Card>


        <ConfirmDialog group="positioned"></ConfirmDialog>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useTaskStatusStore } from '@/stores/taskStatus'
import { useConfirm } from 'primevue/useconfirm'
import { log, scrollToTop } from '@/utils/index'
import RadioButton from 'primevue/radiobutton'
import { useActiveTab } from '@/stores/activeTab'
import { useAuthStore } from '../stores/auth'
import TabNavigation from './TabNavigation.vue'
import Button from 'primevue/button'
import Slider from 'primevue/slider'
import InputNumber from 'primevue/inputnumber'
import Card from 'primevue/card'
import ConfirmDialog from 'primevue/confirmdialog'
import Message from 'primevue/message'
import InlineMessage from 'primevue/inlinemessage'
import SelectButton from 'primevue/selectbutton'
import * as Sentry from '@sentry/vue'
import LocalStorageWrapper from '@/classes/LocalStorageWrapper'
import { PROFILE_TAB_ID, SUBSCRIPTION_TAB_ID } from '@/globals/variables'
import { addStat } from '@/services/stats'
import StatTask from '@/enums/StatsTask'
import InfoTip from '@/components/InfoTip.vue'

const hideUI = ref(true)
const localStorageWrapper = LocalStorageWrapper.getInstance()
const authStore = useAuthStore()
const tabView = useActiveTab()
const confirm = useConfirm()
const taskStatus = useTaskStatusStore()
const allProducts = ref<boolean>(true)
const limit = ref<number>(0)
const maxRelistsPerDay = ref<number>(50)
const delayRange = ref<number[]>([3, 7])
const validations = ref(new Map())
const listingsOlderThan = ref<number>(61)
const closetOldItems = ref<any>([])

//PrimeVue Components Styles

const selectLabelStyles = {
    button: (params: any) => ({
        class: params.context.active
            ? 'bg-[#0070b7] border-[#0070b7] focus:!border-none !px-3'
            : 'bg-gray-100 focus:!border-none focus:!shadow-none !px-3'
    }),
    label: 'text-sm'
}

const sliderStyles = {
    startHandler: { class: 'bg-[#0070b7]' },
    endHandler: { class: 'bg-[#0070b7]' },
    range: { class: 'bg-[#0070b7]' }
}

const updateSliderValue = (val: number[]) => {
    val[0] = Math.floor(val[0])
    val[1] = Math.floor(val[1])
}

onMounted(async () => {
    //load last used settings if found
    await loadRelistSettings()
    hideUI.value = false
})

const loadRelistSettings = async () => {
    try {
        const storedData = await localStorageWrapper.get('relistSettings')
        const relistSettings = JSON.parse(storedData as string)

        if (!relistSettings) return

        closetOldItems.value = relistSettings.closetOldItems ?? []
        listingsOlderThan.value = relistSettings.listingsOlderThan ?? 61
        maxRelistsPerDay.value = relistSettings.maxRelistsPerDay ?? 50
        delayRange.value = relistSettings.delayRange ?? [1, 1]
        limit.value = relistSettings.limit
        allProducts.value = relistSettings.allProducts ?? true

    } catch (err) {
        //failed to acccess localStorage
        Sentry.captureException(err)
    }
}

const sendMessage = (msg: any) => {
    window.parent.postMessage(msg, '*')
}

const validateRelistRequest = () => {
    if (!authStore?.hasActivePlan()) {
        upgradeSubscriptionAlert('top')
        return
    }

    if (taskStatus.relist.status) return

    log('Validating relist request')

    let errorFound = false
    let messages = []

    if (!delayRange.value[0] || delayRange.value[0] < 0)
        messages.push('Delay should be at least 1 second.')
    else if (!delayRange.value[1] || delayRange.value[1] < delayRange.value[0])
        messages.push('Invalid delay range.')

    if (!listingsOlderThan.value || listingsOlderThan.value < 61) messages.push('To comply with Poshmark’s "Excessive Listing Policy", only items listed for at least 61 days can be relisted.')

    validations.value.set('relist', messages)

    if (messages.length) errorFound = true

    // validating share items
    messages = []

    if (!maxRelistsPerDay.value || maxRelistsPerDay.value <= 0) messages.push('Max relist number should be greater than 0')

    if (!maxRelistsPerDay.value || maxRelistsPerDay.value > 150) messages.push('Max relist number should be less than 150')

    if (!allProducts.value && limit.value <= 0) messages.push('Set number of items to relist.')

    validations.value.set('itemsToRelist', messages)

    if (messages.length) errorFound = true

    if (errorFound) {
        scrollToTop()
        return
    }

    //update and send to extension to start task
    const taskData = {
        message: 'relistCloset',
        listingsOlderThan: listingsOlderThan.value,
        maxRelistsPerDay: maxRelistsPerDay.value,
        limit: limit.value,
        allProducts: allProducts.value,
        delay: { min: delayRange.value[0], max: delayRange.value[1] }
    }

    //start message listener
    window.addEventListener('message', relistMessagesHandler)

    sendMessage(taskData)

    taskStatus.changeRelistStatus(true)
    closetOldItems.value = []

    //storing current task configuration to load later to the user
    const config = {
        ...taskData,
        delayRange: delayRange.value,
    }

    //store latest settings in local storage
    localStorageWrapper.set('relistSettings', JSON.stringify(config))
}

const requireConfirmation = (position: any) => {
    confirm.require({
        group: 'positioned',
        icon: 'pi pi-info-circle',
        position: position,
        rejectClass:
            'border border-green-500 font-bold px-8 py-1 bg-green-500 text-white rounded hover:cursor-pointer hover:bg-white hover:text-green-500',
        acceptClass:
            'border border-red-500 font-bold px-8 py-1 bg-red-500 text-white rounded hover:cursor-pointer hover:bg-white hover:text-red-500 outline-0 shadow-none',
        rejectLabel: 'Keep Task running',
        acceptLabel: 'Stop Task',
        header: 'Are you sure you want to stop this task?',
        message: 'Please confirm to proceed.',
        accept: () => {
            sendMessage({ message: 'stopRelistTask' })
        }
    })
}

const upgradeSubscriptionAlert = (position: any) => {
    confirm.require({
        group: 'positioned',
        position: position,
        acceptClass:
            'border border-green-500 font-bold px-8 py-1 bg-green-500 text-white rounded hover:cursor-pointer hover:bg-white hover:text-green-500',
        rejectClass:
            'border border-red-500 font-bold px-8 py-1 bg-red-500 text-white rounded hover:cursor-pointer hover:bg-white hover:text-red-500 outline-0 shadow-none',
        rejectLabel: 'Cancel',
        acceptLabel: authStore.loggedIn ? 'Subscribe' : "Login or Sign Up",
        header: authStore.loggedIn ? 'Upgrade Subscription' : "Login or Sign Up",
        message: authStore.loggedIn ? 'Upgrade subscription or start your free trial to enable this feature.' : "To unlock this feature, please Login or quickly Create an Account. It only takes a minute!",
        accept: () => {
            if (authStore.loggedIn)
                tabView.changeActiveTab(SUBSCRIPTION_TAB_ID)
            else
                tabView.changeActiveTab(PROFILE_TAB_ID)
        }
    })
}

//This will handle messages sent from the extension to the web server
const relistMessagesHandler = async (event: any) => {
    if (event.data.message == 'relistTaskStats') {
        if (event.data.totalItems)
            await addStat({ task_name: StatTask.Relist, total: event.data.totalItems })
    } else if (event.data.message == 'relistTaskEnded') {
        //the stop request was send by the user or if the follow is completed and it is not scheduled to run daily
        if (event.data.requestedByUser || !event.data.isDaily) {
            taskStatus.changeRelistStatus(false)
            taskStatus.changeRelistDaily(false)
            //stop message listener until the task is started again
            window.removeEventListener('message', relistMessagesHandler)
            return
        }
    } else if (event.data.message == 'failedToStartRelistTask') {
        //validations.value.set("followJustJoined", [event.data.error]);
        taskStatus.changeRelistStatus(false)
        taskStatus.changeRelistDaily(false)
        //stop message listener until the task is started again
        window.removeEventListener('message', relistMessagesHandler)
    }
}
</script>

<style lang="scss" scoped>
:deep(.p-radiobutton) {
    .p-radiobutton-box {
        border: 1px solid #0070b7;
        border-radius: 50%;
    }
}
</style>